import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTags, useDeleteTag } from "@/api/tags";
import { CreateTagButton } from "./create-tag-button";
import { EditTagForm } from "./edit-tag-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTagCategories } from "@/api/tags";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tag, TagCategory } from "@/api.types";
export function TagsTable() {
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [filterCategory, setFilterCategory] = useState<string | undefined>(
    undefined
  );
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: tagsResponse, isLoading } = useTags({
    type: filterType === "all" ? undefined : filterType,
    categoryId: filterCategory === "all" ? undefined : filterCategory,
  });
  const { data: categoriesResponse } = useTagCategories();
  const deleteTagMutation = useDeleteTag({
    onSuccess: () => {
      setShowDeleteDialog(false);
    },
  });

  const tags = tagsResponse?.tags ?? [];
  const categories = categoriesResponse?.categories ?? [];

  const selectedTag = tags.find((tag: Tag) => tag.id === selectedTagId) ?? null;

  const handleEdit = (tag: Tag) => {
    setSelectedTagId(tag.id);
    setShowEditForm(true);
  };

  const handleDelete = (tagId: string) => {
    setSelectedTagId(tagId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (selectedTagId) {
      deleteTagMutation.mutate(selectedTagId);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <div className="w-[200px]">
            <Select
              value={filterType}
              onValueChange={(value) => setFilterType(value || undefined)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="SYSTEM">System</SelectItem>
                <SelectItem value="CUSTOMER">Customer</SelectItem>
                <SelectItem value="SHARED">Shared</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[200px]">
            <Select
              value={filterCategory}
              onValueChange={(value) => setFilterCategory(value || undefined)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category: TagCategory) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <CreateTagButton />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Icon</TableHead>
              <TableHead>Color</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  Loading tags...
                </TableCell>
              </TableRow>
            ) : tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  No tags found. Create your first tag.
                </TableCell>
              </TableRow>
            ) : (
              tags.map((tag: Tag) => (
                <TableRow key={tag.id}>
                  <TableCell>{tag.name}</TableCell>
                  <TableCell>{tag.icon ?? "-"}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {tag.color && (
                        <div
                          className="w-5 h-5 rounded-full mr-2"
                          style={{ backgroundColor: tag.color }}
                        ></div>
                      )}
                      {tag.color}
                    </div>
                  </TableCell>
                  <TableCell>{tag.type}</TableCell>
                  <TableCell>{tag.category?.name ?? "-"}</TableCell>
                  <TableCell>{tag.description ?? "-"}</TableCell>
                  <TableCell>
                    <Badge
                      variant={tag.isActive ? "default" : "outline"}
                      className={tag.isActive ? "bg-green-500" : ""}
                    >
                      {tag.isActive ? "ACTIVE" : "INACTIVE"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(tag)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(tag.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Form Dialog */}
      {selectedTag && (
        <EditTagForm
          tag={selectedTag}
          open={showEditForm}
          onOpenChange={setShowEditForm}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the tag
              and remove it from all associated orders and parcels.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              {deleteTagMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
